
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

const MaintenancePage = () => {
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const [targetAt, setTargetAt] = useState(null);

  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });


  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        const res = await fetch("/api/maintenance/status", {
          cache: "no-store",
          headers: { Accept: "application/json" },
        });

        if (!alive) return;

        if (res.ok) {
          const data = await res.json();


          setTargetAt(data?.until || null);
        } else {
          setTargetAt(null);
        }
      } catch {
        if (alive) setTargetAt(null);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, []);


  useEffect(() => {
    const parseTarget = () => {
      if (!targetAt) return null;
      const d = new Date(targetAt);
      if (isNaN(d.getTime())) return null;
      return d.getTime();
    };

    let targetTime = parseTarget();

    const updateTimeLeft = () => {
      if (!targetTime) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const now = Date.now();
      let diff = targetTime - now;
      if (diff < 0) diff = 0;

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [targetAt]);


  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    if (params.get("login") === "1") {
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("open-auth-modal", { detail: { mode: "login" } })
        );
      }, 0);
    }
  }, [location.search]);


  const pageWrapperStyle = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "32px 16px" : "40px 24px",
    position: "relative",
    overflow: "hidden",
  };

  const contentStyle = {
    maxWidth: isTablet ? 720 : 840,
    width: "100%",
    textAlign: "center",
    margin: "0 auto",
    color: "#F9FAFB",
    position: "relative",
    zIndex: 10,
  };

  const topLabelStyle = {
    fontFamily: "Alexandria, system-ui",
    fontWeight: 600,
    fontSize: isMobile ? "14px" : "16px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#9CA3AF",
    marginBottom: isMobile ? 24 : 32,
  };

  const mainTextStyle = {
    fontFamily: "Alexandria, system-ui",
    fontWeight: 800,
    fontSize: isMobile ? "64px" : "96px",
    letterSpacing: "0.08em",
    color: "#F9FAFB",
    marginBottom: isMobile ? 32 : 40,
  };

  const englishDescStyle = {
    fontFamily: "Alexandria, system-ui",
    fontWeight: 500,
    fontSize: isMobile ? "13px" : "14px",
    color: "rgba(226,232,240,0.9)",
    maxWidth: 520,
    margin: "0 auto",
    marginBottom: 12,
  };

  const arabicDescStyle = {
    fontFamily: "Alexandria, system-ui",
    fontWeight: 500,
    fontSize: isMobile ? "13px" : "14px",
    color: "#E5E7EB",
    maxWidth: 520,
    margin: "0 auto",
    marginTop: 8,
    direction: "rtl",
  };

  const arrowStyle = {
    fontSize: 26,
    marginTop: isMobile ? 24 : 30,
    marginBottom: isMobile ? 20 : 26,
    color: "#E5E7EB",
  };

  const xCircleStyle = {
    width: isMobile ? 74 : 86,
    height: isMobile ? 74 : 86,
    borderRadius: "999px",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "radial-gradient(120% 120% at 30% 0%, #1F2937 0%, #020617 45%, #000000 100%)",
    boxShadow: "0 18px 40px rgba(0,0,0,0.65)",
    textDecoration: "none",
  };

  const xLetterStyle = {
    fontFamily: "Alexandria, system-ui",
    fontWeight: 800,
    fontSize: isMobile ? "36px" : "40px",
    color: "#FFFFFF",
  };

  const countdownWrapperStyle = {
    display: "flex",
    justifyContent: "center",
    gap: isMobile ? 24 : 48,
    marginTop: isMobile ? 30 : 40,
    flexWrap: "wrap",
  };

  const countdownItemStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 64,
  };

  const countdownNumberStyle = {
    fontFamily: "Alexandria, system-ui",
    fontWeight: 800,
    fontSize: isMobile ? "26px" : "32px",
    color: "#F9FAFB",
    marginBottom: 4,
  };

  const countdownLabelStyle = {
    fontFamily: "Alexandria, system-ui",
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#9CA3AF",
  };

  const formatNum = (n) => (n < 10 ? `0${n}` : `${n}`);

  return (
    <div style={pageWrapperStyle}>
      <div
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 1 }}
      >
        <img
          src="/images/stars.png"
          alt="Background Stars"
          className="w-full h-full object-cover opacity-80"
        />
      </div>

      <div style={contentStyle}>
        <div style={topLabelStyle}>Something Awesome Is Coming</div>
        <div style={mainTextStyle}>SOON</div>

        <p style={englishDescStyle}>
          We&apos;re performing maintenance to bring you a smoother and better
          experience. The platform will be back online shortly.
        </p>

        <p style={arabicDescStyle}>
          منصّتنا الآن تحت الصيانة لتحسين الأداء وإضافة مميزات جديدة. نرجع لك
          قريب جدًا، شكرًا لصبرك وثقتك.
        </p>



        <div style={arrowStyle}>↓</div>

        <a
          href="https://x.com/Hemh_uj"
          target="_blank"
          rel="noreferrer"
          style={xCircleStyle}
        >
          <span style={xLetterStyle}>X</span>
        </a>

        <div style={countdownWrapperStyle}>
          <div style={countdownItemStyle}>
            <div style={countdownNumberStyle}>{formatNum(timeLeft.days)}d</div>
            <div style={countdownLabelStyle}>DAYS</div>
          </div>
          <div style={countdownItemStyle}>
            <div style={countdownNumberStyle}>{formatNum(timeLeft.hours)}h</div>
            <div style={countdownLabelStyle}>HOURS</div>
          </div>
          <div style={countdownItemStyle}>
            <div style={countdownNumberStyle}>
              {formatNum(timeLeft.minutes)}m
            </div>
            <div style={countdownLabelStyle}>MINUTES</div>
          </div>
          <div style={countdownItemStyle}>
            <div style={countdownNumberStyle}>
              {formatNum(timeLeft.seconds)}s
            </div>
            <div style={countdownLabelStyle}>SECONDS</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenancePage;
